import React, { useState, useEffect, useCallback } from 'react';
import companyService, { Company, CreateCompanyPayload, UpdateCompanyPayload } from '../../services/companyService';
import userService from '../../services/userService';
import Swal from 'sweetalert2';

const CompanyManagement: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [formData, setFormData] = useState<CreateCompanyPayload>({
    name: '',
    email: '',
    phone: '',
  });

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await companyService.listCompanies({
        search: searchTerm,
        page: currentPage,
        limit: limit,
      });
      
      const companiesData = Array.isArray(response.data) ? response.data : [];
      setCompanies(companiesData);
      setTotal(response.total || 0);
      setTotalPages(response.totalPages || 1);
    } catch (error: any) {
      Swal.fire('Erro', 'Erro ao carregar empresas', 'error');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, limit]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleOpenModal = (company?: Company) => {
    if (company) {
      setCurrentCompany(company);
      setFormData({
        name: company.name,
        email: company.email,
        phone: company.phone,
      });
    } else {
      setCurrentCompany(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentCompany(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      Swal.fire('Erro', 'Nome da empresa é obrigatório', 'error');
      return false;
    }
    if (!formData.email.trim()) {
      Swal.fire('Erro', 'E-mail é obrigatório', 'error');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      Swal.fire('Erro', 'E-mail inválido', 'error');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      if (currentCompany) {
        // Update
        const updatePayload: UpdateCompanyPayload = {};
        if (formData.name !== currentCompany.name) updatePayload.name = formData.name;
        if (formData.email !== currentCompany.email) updatePayload.email = formData.email;
        if (formData.phone !== currentCompany.phone) updatePayload.phone = formData.phone;

        await companyService.updateCompany(currentCompany.id, updatePayload);
        Swal.fire('Sucesso', 'Empresa atualizada com sucesso', 'success');
        loadCompanies();
      } else {
        // Create
        const newCompany = await companyService.createCompany(formData);
        Swal.fire('Sucesso', `Empresa criada com sucesso! ID: ${newCompany.id}`, 'success');
        loadCompanies();
      }
      handleCloseModal();
    } catch (error: any) {
      console.error('Error saving company:', error);
      Swal.fire('Erro', error.response?.data?.error || 'Erro ao salvar empresa', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (company: Company) => {
    try {
      // Get all users (admin can see all users) and count those from this company
      const usersResponse = await userService.listUsers();
      const companyUsers = usersResponse.data.filter(user => user.company_id === company.id);
      const usersCount = companyUsers.length;
      
      const result = await Swal.fire({
        title: 'Confirmar exclusão?',
        html: usersCount > 0
          ? `<p>A empresa "<strong>${company.name}</strong>" possui <strong>${usersCount}</strong> usuário(s) cadastrado(s).</p>
             <p class="text-danger"><i class="fas fa-exclamation-triangle"></i> Todos os usuários serão excluídos junto com a empresa!</p>
             <p>Deseja realmente continuar?</p>`
          : `<p>Deseja realmente excluir a empresa "<strong>${company.name}</strong>"?</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sim, excluir',
        cancelButtonText: 'Cancelar',
        customClass: {
          popup: 'swal-wide',
        },
      });

      if (result.isConfirmed) {
        await companyService.deleteCompany(company.id);
        loadCompanies();
        Swal.fire('Excluído', 'Empresa excluída com sucesso', 'success');
      }
    } catch (error: any) {
      console.error('Error deleting company:', error);
      Swal.fire('Erro', error.response?.data?.error || 'Erro ao excluir empresa', 'error');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on search
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <li key="prev" className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <i className="fas fa-chevron-left"></i>
        </button>
      </li>
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <li key={1} className="page-item">
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>
      );
      if (startPage > 2) {
        pages.push(
          <li key="ellipsis1" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
    }

    // Numbered pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key="ellipsis2" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
      pages.push(
        <li key={totalPages} className="page-item">
          <button className="page-link" onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </button>
        </li>
      );
    }

    // Next button
    pages.push(
      <li key="next" className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <i className="fas fa-chevron-right"></i>
        </button>
      </li>
    );

    return (
      <nav aria-label="Page navigation">
        <ul className="pagination pagination-sm mb-0">{pages}</ul>
      </nav>
    );
  };

  return (
    <>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Gerenciamento de Empresas</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/dashboard">Home</a></li>
                <li className="breadcrumb-item active">Empresas</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <div className="row align-items-center">
                <div className="col-md-4">
                  <h3 className="card-title">
                    <i className="fas fa-building mr-2"></i>
                    Lista de Empresas
                  </h3>
                </div>
                <div className="col-md-4">
                  <div className="input-group input-group-sm">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar empresa..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 text-right">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleOpenModal()}
                  >
                    <i className="fas fa-plus mr-1"></i>
                    Nova Empresa
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Carregando...</span>
                  </div>
                  <p className="mt-2 text-muted">Carregando empresas...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>Nome</th>
                        <th>E-mail</th>
                        <th>Telefone</th>
                        <th className="text-center" style={{ width: '100px' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companies.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center py-4 text-muted">
                            <i className="fas fa-inbox fa-2x mb-3 d-block"></i>
                            {searchTerm ? 'Nenhuma empresa encontrada para sua busca' : 'Nenhuma empresa cadastrada'}
                          </td>
                        </tr>
                      ) : (
                        companies.map((company) => (
                          <tr key={company.id}>
                            <td className="align-middle">
                              <strong>{company.name}</strong>
                            </td>
                            <td className="align-middle">
                              <a href={`mailto:${company.email}`}>{company.email}</a>
                            </td>
                            <td className="align-middle">{company.phone || '-'}</td>
                            <td className="text-center align-middle">
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-info"
                                  onClick={() => handleOpenModal(company)}
                                  title="Editar"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-danger"
                                  onClick={() => handleDelete(company)}
                                  title="Excluir"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {!loading && companies.length > 0 && (
              <div className="card-footer clearfix">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <small className="text-muted">
                      Mostrando {companies.length} de {total} empresa(s)
                    </small>
                  </div>
                  <div className="col-md-6">
                    <div className="float-right">
                      {renderPagination()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h4 className="modal-title">
                    {currentCompany ? 'Editar Empresa' : 'Nova Empresa'}
                  </h4>
                  <button
                    type="button"
                    className="close"
                    onClick={handleCloseModal}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Nome *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>E-mail *</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Telefone</label>
                    <input
                      type="text"
                      className="form-control"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={handleCloseModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompanyManagement;
